import { useEffect, useRef, useState } from 'react';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import { Button } from '@/components/ui/button';

export function ReviewPdfViewer({ url }: { url: string }) {
  const [document, setDocument] = useState<PDFDocumentProxy | null>(null);
  const [page, setPage] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [attempt, setAttempt] = useState(0);
  const [error, setError] = useState('');
  const [rendering, setRendering] = useState(true);
  const [width, setWidth] = useState(600);
  const container = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const observer = new ResizeObserver(entries => setWidth(Math.max(200, entries[0].contentRect.width - 24)));
    if (container.current) observer.observe(container.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let active = true;
    let destroy: (() => void) | undefined;
    setDocument(null); setPage(1); setError(''); setRendering(true);
    void import('pdfjs-dist').then(async pdfjs => {
      if (!active) return;
      pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;
      const task = pdfjs.getDocument({ url, useSystemFonts: true, useWasm: false });
      destroy = () => { void task.destroy(); };
      const loaded = await task.promise;
      if (active) setDocument(loaded);
    }).catch(() => {
      if (active) { setError('Não foi possível carregar o PDF. Tente novamente ou abra o arquivo em outra aba.'); setRendering(false); }
    });
    return () => { active = false; destroy?.(); };
  }, [url, attempt]);

  useEffect(() => {
    if (!document) return;
    let active = true;
    let cancel: (() => void) | undefined;
    setRendering(true); setError('');
    void document.getPage(page).then(async pdfPage => {
      if (!active || !canvas.current) return;
      const base = pdfPage.getViewport({ scale: 1 });
      const scale = Math.min(width / base.width, 2) * zoom;
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      const viewport = pdfPage.getViewport({ scale: scale * ratio });
      canvas.current.width = Math.ceil(viewport.width);
      canvas.current.height = Math.ceil(viewport.height);
      canvas.current.style.width = `${viewport.width / ratio}px`;
      canvas.current.style.height = `${viewport.height / ratio}px`;
      const task = pdfPage.render({ canvas: canvas.current, viewport });
      cancel = () => task.cancel();
      await task.promise;
      if (active) setRendering(false);
    }).catch(() => {
      if (active) { setError('Não foi possível exibir esta página do PDF.'); setRendering(false); }
    });
    return () => { active = false; cancel?.(); };
  }, [document, page, zoom, width]);

  return <div className="flex h-full min-h-0 w-full flex-col bg-muted/30" aria-label="Visualizador do documento PDF">
    <div className="flex flex-wrap items-center justify-center gap-2 border-b bg-background p-2 text-xs">
      <Button variant="outline" size="sm" disabled={!document || page <= 1} onClick={() => setPage(p => p - 1)}>Página anterior</Button>
      <span>Página {page} de {document?.numPages ?? '…'}</span>
      <Button variant="outline" size="sm" disabled={!document || page >= document.numPages} onClick={() => setPage(p => p + 1)}>Próxima página</Button>
      <Button variant="outline" size="sm" aria-label="Diminuir zoom" disabled={zoom <= 0.5} onClick={() => setZoom(z => Math.max(0.5, z - 0.25))}>−</Button>
      <span>{Math.round(zoom * 100)}%</span>
      <Button variant="outline" size="sm" aria-label="Aumentar zoom" disabled={zoom >= 3} onClick={() => setZoom(z => Math.min(3, z + 0.25))}>+</Button>
    </div>
    <div ref={container} className="min-h-0 flex-1 overflow-auto p-3">
      {rendering && !error && <p role="status" className="mb-2 text-center text-sm">Carregando página do PDF…</p>}
      {error && <div role="alert" className="space-y-3 p-4 text-sm"><p>{error}</p><Button variant="outline" onClick={() => setAttempt(a => a + 1)}>Tentar carregar PDF novamente</Button></div>}
      <canvas ref={canvas} aria-label={`Página ${page} do documento`} className={`mx-auto bg-white shadow ${error || rendering ? 'opacity-0' : ''}`} />
    </div>
  </div>;
}
